<script setup lang="ts">
  import { computed } from 'vue'
  import { User, Building2, Link2 } from 'lucide-vue-next'
  import type { ActaportPotentialMatch } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    match: ActaportPotentialMatch
    isSelected: boolean
    showLinkedBadge?: boolean
  }>()

  const emit = defineEmits<{
    (e: 'select'): void
  }>()

  const { t } = useTranslation({
    en: {
      linked: 'Linked',
      email: 'Email',
      phoneNumber: 'Phone number',
      dateOfBirth: 'Date of Birth',
      address: 'Address',
      mandant: 'Client',
      arbeitnehmer: 'Employee'
    },
    de: {
      linked: 'Verknüpft',
      email: 'E-Mail',
      phoneNumber: 'Telefonnummer',
      dateOfBirth: 'Geburtsdatum',
      address: 'Adresse',
      mandant: 'Mandant',
      arbeitnehmer: 'Arbeitnehmer'
    }
  })

  const isCompany = computed(() => {
    return props.match.contact_type === 'JURISTISCHE_PERSON'
  })

  const formattedDateOfBirth = computed(() => {
    if (!props.match.date_of_birth) return ''
    const date = new Date(props.match.date_of_birth)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  })

  const isFieldMatched = (fieldName: string): boolean => {
    return props.match.matched_fields.includes(fieldName)
  }

  const getFieldHighlightClass = (fieldName: string): string => {
    if (isFieldMatched(fieldName)) {
      return 'rounded bg-green-100 px-1.5 py-0.5 text-green-800'
    }
    return ''
  }
</script>

<template>
  <div
    :class="[
      `min-h-[140px] cursor-pointer rounded-lg border-2 bg-white p-4 outline outline-2
      -outline-offset-[4px] transition-all hover:shadow-md`,
      isSelected ? 'border-purple-500 outline-purple-200' : 'border-gray-200 outline-transparent'
    ]"
    @click="emit('select')"
  >
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 pt-1">
        <div
          :class="[
            'flex h-4 w-4 items-center justify-center rounded-full border-2',
            isSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
          ]"
        >
          <div
            v-if="isSelected"
            class="h-2 w-2 rounded-full bg-white"
          />
        </div>
      </div>

      <div class="flex-shrink-0 pt-2">
        <Building2
          v-if="isCompany"
          class="h-5 w-5 text-gray-600"
        />
        <User
          v-else
          class="h-5 w-5 text-gray-600"
        />
      </div>

      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold text-gray-900">
              {{ match.display_name }}
            </h3>
            <p class="text-sm text-gray-500">{{ t('mandant') }}, {{ t('arbeitnehmer') }}</p>
          </div>

          <div class="flex-shrink-0">
            <span
              v-if="showLinkedBadge && isSelected"
              class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium
                text-blue-800"
            >
              <Link2 class="h-3 w-3" />
              {{ t('linked') }}
            </span>
          </div>
        </div>

        <div class="mt-3 space-y-1 text-sm">
          <div
            v-if="match.email"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('email') }}:</span>
            <span :class="['truncate', getFieldHighlightClass('email')]">
              {{ match.email }}
            </span>
          </div>
          <div
            v-if="match.phone"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('phoneNumber') }}:</span>
            <span :class="[getFieldHighlightClass('phone')]">
              {{ match.phone }}
            </span>
          </div>
          <div
            v-if="formattedDateOfBirth"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('dateOfBirth') }}:</span>
            <span :class="[getFieldHighlightClass('date_of_birth')]">
              {{ formattedDateOfBirth }}
            </span>
          </div>
          <div
            v-if="match.address"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('address') }}:</span>
            <span :class="[getFieldHighlightClass('address')]">
              {{ match.address }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
